<i18n lang="yaml" locale="en">
  filename asc: Filename ascending
  filename desc: Filename descending
  label: Label
  order by: Order by
  name: Name
</i18n>

<i18n lang="yaml" locale="de">
  filename asc: Dateiname aufsteigend
  filename desc: Dateiname absteigend
  label: Label
  order by: Sortieren nach
  name: Name
</i18n>

<template>
  <div
    :class="{folder: true, root}"
  >
    <dropzone
      @drop="onDrop($event)"
      :validation-fn="validateDrag"
    >
      {{ tooltip }}
    </dropzone>
    <div
      v-if="!root"
      class="headline"
      @click="toggleSelected"
    >
      <binder
        @click.native.stop="toggleOpen()"
        :active="open"
        class="folder-icon"
        color="white"
        size="s"
      />
      <span
        class="folder-name"
        draggable
        v-draggable="$store.getters.path"
      >
        {{ name }}
      </span>
      <add-circle
        size="xs"
        class="add-folder-icon"
        @click.native.stop="openNewFolderPrompt()"
      />
      <settings
        size="xs"
        class="edit-folder-icon"
        @click.native.stop="handleEdit()"
      />
      <delete
        size="xs"
        class="delete-folder-icon"
        color="danger"
        @click.native.stop="deleteFolder()"
      />
    </div>
    <div
      v-if="open || root"
      class="folder-content"
    >
      <set-class
        :func="selectedFunc"
      >
        <order-by
          :func="orderFunc"
        >
          <component
            v-for="child in children"
            :key="child.getters.path"
            :path="child.getters.path"
            :is="child.getters.type"
            v-bind="child.getters.props"
          />
        </order-by>
      </set-class>
    </div>
  </div>
</template>

<script>
import Binder from '@/components/icons/Binder'
import AddCircle from '@/components/icons/AddCircle'
import Settings from '@/components/icons/Settings'
import Delete from '@/components/icons/Delete'
import { Confirm, Prompt, Async, Info, Warning } from '@/plugins/snotify'
import Dropzone from '@/components/editor/Dropzone'
import axios from 'axios'
import md5 from 'md5-webworker'
import lookupMimetype from '@/components/modules/Files/mimetypes'
import draggable from '@/directives/draggable'
import OrderBy from '@/components/utils/OrderBy'
import SetClass from '@/components/utils/SetClass'
import { getStoreFromVnode, guessMimetype } from '@/utils'
import { NotSupported } from '@/errors'
import BaseModule from '@/components/modules/BaseModule'
import softLimits from '@/projectSoftLimits'

export default {
  // eslint-disable-next-line vue/name-property-casing
  name: 'folder',
  extends: BaseModule,
  components: {
    Binder,
    AddCircle,
    Settings,
    Delete,
    Dropzone,
    OrderBy,
    SetClass
  },
  directives: {
    draggable
  },
  inject: ['$project'],
  props: {
    name: {
      type: String,
      required: true,
      input: 'text',
      label: 'name'
    },
    root: {
      type: Boolean,
      required: false,
      default: false
    },
    open: {
      type: Boolean,
      default: false
    },
    // eslint-disable-next-line
    _headlineorderBy: {
      input: 'heading',
      label: 'order by'
    },
    orderBy: {
      type: String,
      default: 'filename_asc',
      validator (value) {
        return ['filename_asc', 'filename_desc'].includes(value)
      },
      input: 'radio',
      choices: {
        filename_asc: 'filename asc',
        filename_desc: 'filename desc'
      }
    }
  },
  data () {
    return {
      tooltip: ''
    }
  },
  computed: {
    orderFunc () {
      switch (this.orderBy) {
        case 'filename_desc':
          return (v1, v2) => {
            const $v1 = getStoreFromVnode(this.$store, v1)
            const $v2 = getStoreFromVnode(this.$store, v2)
            if ($v1.getters.type.name === this.$options.name && $v2.getters.type.name !== this.$options.name) {
              return -1
            } else if ($v1.getters.type.name !== this.$options.name && $v2.getters.type.name === this.$options.name) {
              return 1
            } else {
              return $v2.getters.props.name.localeCompare($v1.getters.props.name)
            }
          }
        case 'filename_asc':
        default:
          return (v1, v2) => {
            const $v1 = getStoreFromVnode(this.$store, v1)
            const $v2 = getStoreFromVnode(this.$store, v2)
            if ($v1.getters.type.name === this.$options.name && $v2.getters.type.name !== this.$options.name) {
              return -1
            } else if ($v1.getters.type.name !== this.$options.name && $v2.getters.type.name === this.$options.name) {
              return 1
            } else {
              return $v1.getters.props.name.localeCompare($v2.getters.props.name)
            }
          }
      }
    }
  },
  methods: {
    selectedFunc (vnode) {
      if (this.$root.$store.getters.isAssetSelected(vnode.componentOptions.propsData.path)) {
        return { selected: true, included: false }
      } else if (getStoreFromVnode(this.$store, vnode).getters.type.name !== this.$options.name && this.$root.$store.getters.isAssetSelected(this.$store.getters.path)) {
        return { selected: false, included: true }
      } else {
        return { selected: false, included: false }
      }
    },
    toggleSelected () {
      this.$root.$store.dispatch('toggleAssetSelected', {
        path: this.$store.getters.path
      })
    },
    validateDrag (event) {
      if (event.dataTransfer.types.includes('assets')) {
        this.tooltip = 'Verschieben'
        const paths = Object.keys(this.$root.$store.getters.selectedAssets)
        const accepted = paths.filter(path => {
          const module = this.$store.getters.modulesByPath[path]
          // only allow acceptedFileTypes
          return module.getters.type.name !== 'folder' || !this.$store.getters.path.startsWith(path)
        })
        return accepted.length > 0
      } else if (event.dataTransfer.types.includes('Files')) {
        this.tooltip = 'Hochladen'
        return true
      }
    },
    handleEdit () {
      this.$router.replace({ query: { edit: this.$store.getters.path } })
    },
    openNewFolderPrompt () {
      new Prompt('Name', 'Bitte geben Sie den Namen des Ordners an.', this.addSubFolder).open()
    },
    addSubFolder (name) {
      this.$store.dispatch('addChild', {
        childType: 'folder',
        props: {
          name
        }
      })
    },
    deleteSubFolder (name) {
      const childPath = this.$store.getters.childrenByName[name].getters.path
      if (this.$root.$store.getters.isAssetSelected(childPath)) {
        this.$root.$store.dispatch('toggleAssetSelected', {
          path: childPath
        })
      }
      this.$store.dispatch('removeChild', {
        name: name
      })
    },
    deleteFolder () {
      new Confirm('Achtung!', 'Der Ordner und alle darin enthaltenen Dateien werden aus dem Projekt entfernt. Fortfahren?', () => {
        // parent is component loader, but we want the next folder
        this.$parent.deleteSubFolder(this.$store.getters.name)
      }).open()
    },
    toggleOpen () {
      this.$store.dispatch('setProp', {
        key: 'open',
        value: !this.open
      })
    },
    async onDrop (event) {
      // this is an internal drop
      if (event.dataTransfer.getData('assets')) {
        const assets = this.$root.$store.getters.selectedAssets
        for (const target in assets) {
          for (const asset in assets) {
            if (target.startsWith(asset) && asset !== target) {
              delete assets[target]
            }
          }
        }
        for (const asset in assets) {
          this.$store.dispatch('moveFrom', {
            path: asset
          })
        }
        this.$root.$store.dispatch('deselectAllAssets')
      }
      // this is an external file drop
      if (event.dataTransfer.files.length > 0) {
        this.upload(event.dataTransfer.files)
      }
    },
    async upload (files) {
      for (const f of files) {
        new Async('Die Datei ' + f.name + ' wird hochgeladen...', 'Die Datei ' + f.name + ' wurde hochgeladen.', 'Die Datei ' + f.name + ' konnte nicht hochgeladen werden.')
          .open(new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = async () => {
              let file = f
              try {
                file = new File([f], f.name, { type: await guessMimetype(reader.result) })
              } catch (e) {
                if (e instanceof NotSupported) {
                  new Info('Dieser Dateityp wird nicht unterstützt.').open()
                  reject(e)
                  return
                } else {
                  throw e
                }
              }
              const hash = await md5(file)
              try {
                await axios.head(this.url(hash), {
                  headers: {
                    'Cache-Control': 'no-cache'
                  }
                })
              } catch (e) {
                if (e.response.status === 404) {
                  const formData = new FormData()
                  formData.append('file', file)
                  try {
                    const resp = await axios.post('/assets', formData)
                    if (resp.data.length / (1024 * 1024) > softLimits.fileSize) {
                      new Warning(`Die empfohlene Dateigröße (${softLimits.fileSize} MB) wurde überschritten...`).open()
                    }
                  } catch (e) {
                    reject(e)
                  }
                } else {
                  throw e
                }
              }
              this.$store.dispatch('addChild', {
                childType: lookupMimetype(file.type).name,
                props: {
                  name: file.name,
                  src: this.url(hash)
                }
              })
              resolve()
            }
            this.$nextTick(() => {
              reader.readAsArrayBuffer(f)
            })
          }))
      }
    },
    url (md5) {
      return '/assets/' + md5
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/uiColors';

.folder {
  position: relative;
  cursor: pointer;

  &.root {
    height: 100%;
  }

  .headline {
    display: flex;
    align-items: center;
    width: calc(100% - 1px);
    padding: 10px 0 10px 1px;
    border-bottom: 1px solid rgba($color: #fff, $alpha: 0.05);

    &:hover {
      color: $color-main;
    }

    .folder-name {
      margin-right: auto;
    }

    .edit-folder-icon,
    .delete-folder-icon,
    .folder-name {
      margin-left: 10px;
    }
  }

  .folder-content {
    /deep/ .folder-content {
      padding-left: 20px;
      border-left: 2px dotted rgba($color: $color-main, $alpha: 0.5);
      margin: 15px 0;
    }

    /deep/ .headline > .folder-icon {
      margin-top: -3px;
    }

    /deep/ .selected > .content,
    /deep/ .selected > .headline > .folder-icon {
      box-shadow: 0 0 0 1px $color-success !important;
    }

    /deep/ .included > .content {
      box-shadow: 0 0 0 1px $color-success !important;
    }

    /deep/ > .asset-browser-item {
      display: flex;
      width: calc(100% - 1px);
      padding: 10px 0 10px 1px;
      overflow: visible;
      cursor: pointer;
      align-items: center;
      border-bottom: 1px solid rgba($color: #fff, $alpha: 0.05);

      > .name {
        word-break: break-all;
        margin-right: auto;
        padding-right: 4rem;
        font-size: 0.75rem;
        line-height: 1rem;
        font-weight: 300;
        color: rgba($color: #fff, $alpha: 0.6);
      }

      > .content {
        min-width: 48px;
        max-width: 48px;
        height: auto !important;
        margin-right: 10px;

        > .page,
        .canvasWrapper,
        canvas {
          min-width: 48px;
          max-width: 48px;
          height: auto !important;
          vertical-align: bottom;
        }
      }
    }
  }
}
</style>
